/**
 * useEventSource — React hook for SSE with automatic reconnection.
 *
 * Replaces raw EventSource + polling fallback with a single, reliable mechanism.
 * - Typed event dispatch via callback
 * - Automatic reconnection with exponential backoff (1s → 2s → 4s, max 30s)
 * - Connection state exposed for UI indicators
 * - Clean teardown on unmount or URL change
 * - Last-Event-ID sent on reconnect for server-side replay
 */

import { useEffect, useRef, useCallback, useState } from "react";

export type SSEStatus = "connecting" | "open" | "closed" | "error";

interface UseEventSourceOptions {
  /** SSE endpoint URL. Pass null/undefined to disable. */
  url: string | null | undefined;
  /** Called for each parsed SSE message. */
  onEvent: (event: Record<string, unknown>) => void;
  /** Called on connection state changes. */
  onStatusChange?: (status: SSEStatus) => void;
  /** Called when the connection encounters an error (for logging/toasts). */
  onError?: (error: Event) => void;
}

export function useEventSource({ url, onEvent, onStatusChange, onError }: UseEventSourceOptions) {
  const [status, setStatus] = useState<SSEStatus>("closed");
  const esRef = useRef<EventSource | null>(null);
  const retriesRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastEventIdRef = useRef<string | null>(null);

  // Stable refs for callbacks to avoid reconnection on callback identity changes
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;
  const onStatusChangeRef = useRef(onStatusChange);
  onStatusChangeRef.current = onStatusChange;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const updateStatus = useCallback((s: SSEStatus) => {
    setStatus(s);
    onStatusChangeRef.current?.(s);
  }, []);

  useEffect(() => {
    if (!url) {
      updateStatus("closed");
      return;
    }

    let cancelled = false;

    function connect() {
      if (cancelled) return;

      // Append Last-Event-ID as query param (browser also sends the header,
      // but query param is a backup for proxies that strip custom headers)
      let connectUrl = url!;
      if (lastEventIdRef.current) {
        const sep = connectUrl.includes("?") ? "&" : "?";
        connectUrl += `${sep}lastEventId=${encodeURIComponent(lastEventIdRef.current)}`;
      }

      updateStatus("connecting");
      const es = new EventSource(connectUrl);
      esRef.current = es;

      es.onopen = () => {
        if (cancelled) { es.close(); return; }
        retriesRef.current = 0;
        updateStatus("open");
      };

      es.onmessage = (e) => {
        if (cancelled) return;
        // Track last event ID for reconnection replay
        if (e.lastEventId) {
          lastEventIdRef.current = e.lastEventId;
        }
        try {
          const data = JSON.parse(e.data);
          onEventRef.current(data);
        } catch {
          // Skip malformed messages (e.g. keepalive comments don't trigger onmessage)
        }
      };

      es.onerror = (err) => {
        if (cancelled) { es.close(); return; }
        es.close();
        esRef.current = null;
        updateStatus("error");
        onErrorRef.current?.(err);

        // Exponential backoff: 1s, 2s, 4s, 8s, ... max 30s
        const delay = Math.min(1000 * Math.pow(2, retriesRef.current), 30_000);
        retriesRef.current++;
        retryTimerRef.current = setTimeout(connect, delay);
      };
    }

    connect();

    return () => {
      cancelled = true;
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
      updateStatus("closed");
    };
  }, [url, updateStatus]);

  return { status };
}
